import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Search,
  Plus,
  ChefHat,
  RefreshCw,
  X,
  Loader2,
  ArrowUpDown,
  ChevronDown,
  Grid3x3,
  Filter,
  SlidersHorizontal,
  TrendingUp,
  TrendingDown,
  Percent,
  DollarSign,
  Layers,
  RotateCcw,
  Check,
  Building2,
} from 'lucide-react';
import RecipeCard from './RecipeCard';
import RecipeEditor from './RecipeEditor';
import RecipeViewer from './RecipeViewer';
import { useMenuItems } from '../../../../hooks/useMenuItems';
import useRecipe from '../../../../hooks/useReciepe'; // 👈 note spelling
import categoryService from '../../../../services/categoryService';
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import { P } from '@/config/permissionCodes';

import toast from 'react-hot-toast';
import { ConfirmDialog } from '../../Menu_Management/UIComponents';

/**
 * Recipe List Component with Advanced Filtering & Category Sidebar
 */

// Sort options
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'cost_asc', label: 'Cost (Low to High)' },
  { value: 'cost_desc', label: 'Cost (High to Low)' },
  { value: 'profit_asc', label: 'Profit (Low to High)' },
  { value: 'profit_desc', label: 'Profit (High to Low)' },
  { value: 'foodcost_asc', label: 'Food Cost % (Low to High)' },
  { value: 'foodcost_desc', label: 'Food Cost % (High to Low)' },
  { value: 'updated_desc', label: 'Recently Updated' },
  { value: 'updated_asc', label: 'Oldest Updated' },
];

// Quick filter presets
const QUICK_FILTERS = [
  {
    id: 'high_profit',
    label: 'High Profit',
    icon: TrendingUp,
    color: 'emerald',
    description: 'Profit margin > 60%',
    filter: (recipe) => {
      const profit = recipe.sellingPrice - recipe.totalCost;
      const margin = recipe.sellingPrice > 0 ? (profit / recipe.sellingPrice) * 100 : 0;
      return margin > 60;
    },
  },
  {
    id: 'low_profit',
    label: 'Low Profit',
    icon: TrendingDown,
    color: 'amber',
    description: 'Profit margin < 30%',
    filter: (recipe) => {
      const profit = recipe.sellingPrice - recipe.totalCost;
      const margin = recipe.sellingPrice > 0 ? (profit / recipe.sellingPrice) * 100 : 0;
      return margin < 30 && margin >= 0;
    },
  },
  {
    id: 'low_food_cost',
    label: 'Low Food Cost',
    icon: Percent,
    color: 'teal',
    description: 'Food cost < 30%',
    filter: (recipe) => {
      const foodCostPct = recipe.sellingPrice > 0 ? (recipe.totalCost / recipe.sellingPrice) * 100 : 0;
      return foodCostPct < 30;
    },
  },
  {
    id: 'high_food_cost',
    label: 'High Food Cost',
    icon: Percent,
    color: 'red',
    description: 'Food cost > 40%',
    filter: (recipe) => {
      const foodCostPct = recipe.sellingPrice > 0 ? (recipe.totalCost / recipe.sellingPrice) * 100 : 0;
      return foodCostPct > 40;
    },
  },
  {
    id: 'with_variants',
    label: 'With Variants',
    icon: Layers,
    color: 'violet',
    description: 'Recipes with variant',
    filter: (recipe) => recipe.variantId != null,
  },
  {
    id: 'no_ingredients',
    label: 'No Ingredients',
    icon: ChefHat,
    color: 'slate',
    description: 'Missing ingredients',
    filter: (recipe) => !recipe.ingredients || recipe.ingredients.length === 0,
  },
];

// ── Access denied screen ─────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view recipes.</p>
      </div>
    </div>
  );
}

export default function RecipeList() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permission checks ──────────────────────────────────────────────────────
  const canViewScreen = hasPermission(P.INVENTORY.RECIPES.SCREEN);
  const canAdd        = hasPermission(P.INVENTORY.RECIPES.ADD);
  const canEdit       = hasPermission(P.INVENTORY.RECIPES.EDIT);
  const canDuplicate  = hasPermission(P.INVENTORY.RECIPES.DUPLICATE);
  const canDelete     = hasPermission(P.INVENTORY.RECIPES.DELETE);

  // Screen-level guard
  if (!canViewScreen) return <AccessDenied />;

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const { items: menuItems, loading: menuLoading, refreshItems } = useMenuItems();

  // 🔗 useRecipe hook
  const {
    recipes = [],
    recipesLoading,
    fetchRecipes,
    createRecipe,
    updateRecipe,
    deleteRecipe,
  } = useRecipe();

  // Data State
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);
  const [isLoading, setIsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [costRange, setCostRange] = useState({ min: '', max: '' });
  const [profitRange, setProfitRange] = useState({ min: '', max: '' });
  const [foodCostRange, setFoodCostRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('name_asc');
  const [activeQuickFilters, setActiveQuickFilters] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // UI State
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef(null);
  const advancedFilterRef = useRef(null);

  // Modal State
  const [editorOpen, setEditorOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [duplicateIngredients, setDuplicateIngredients] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: '',
    data: null,
  });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  // Fetch categories on mount and when branch changes
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const data = await categoryService.getAll(selectedBranchId);
        setCategories(data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [selectedBranchId]);

  // Fetch recipes on mount and when branch changes
  useEffect(() => {
    fetchRecipes(selectedBranchId);
  }, [fetchRecipes, selectedBranchId]);

  // DEBUG: Log category selection + recipe categories
  useEffect(() => {
    console.log("selectedCategory:", selectedCategory);
    console.log(
      "recipes:",
      recipes.map(r => ({
        id: r.id,
        name: r.menuItemName,
        categoryId: r.categoryId,
      }))
    );
  }, [selectedCategory, recipes]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
      if (advancedFilterRef.current && !advancedFilterRef.current.contains(e.target)) {
        setShowAdvancedFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle quick filter
  const toggleQuickFilter = (filterId) => {
    setActiveQuickFilters(prev =>
      prev.includes(filterId)
        ? prev.filter(id => id !== filterId)
        : [...prev, filterId]
    );
  };

  // Helper to get categoryId of a recipe using menuItems fallback
  const getRecipeCategoryId = (recipe) => {
    if (recipe.categoryId != null) return recipe.categoryId;
    const mi = menuItems.find(
      (m) => Number(m.id) === Number(recipe.menuItemId)
    );
    return mi?.categoryId ?? null;
  };

  // Get recipe count per category
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return recipes.length;
    return recipes.filter((r) => {
      const rid = getRecipeCategoryId(r);
      return rid != null && Number(rid) === Number(categoryId);
    }).length;
  };

  // Check if category has recipes matching search
  const categoryHasSearchMatch = (categoryId) => {
    if (!searchQuery.trim()) return false;
    return recipes.some((recipe) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        recipe.menuItemName?.toLowerCase().includes(q) ||
        recipe.ingredients?.some((ing) => ing.name?.toLowerCase().includes(q));
      const recipeCatId = getRecipeCategoryId(recipe);
      const matchesCategory =
        categoryId === 'all' ||
        (recipeCatId != null && Number(recipeCatId) === Number(categoryId));
      return matchesSearch && matchesCategory;
    });
  };

  // Check if any filter is active (excluding category)
  const hasActiveFilters = useMemo(
    () =>
      searchQuery ||
      costRange.min ||
      costRange.max ||
      profitRange.min ||
      profitRange.max ||
      foodCostRange.min ||
      foodCostRange.max ||
      activeQuickFilters.length > 0,
    [searchQuery, costRange, profitRange, foodCostRange, activeQuickFilters]
  );

  // Check if advanced range filters are active
  const hasAdvancedFilters = useMemo(
    () =>
      costRange.min ||
      costRange.max ||
      profitRange.min ||
      profitRange.max ||
      foodCostRange.min ||
      foodCostRange.max,
    [costRange, profitRange, foodCostRange]
  );

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (costRange.min || costRange.max) count++;
    if (profitRange.min || profitRange.max) count++;
    if (foodCostRange.min || foodCostRange.max) count++;
    count += activeQuickFilters.length;
    return count;
  }, [searchQuery, costRange, profitRange, foodCostRange, activeQuickFilters]);

  // Filter and sort recipes
  const filteredRecipes = useMemo(() => {
    let result = recipes.filter((recipe) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = recipe.menuItemName?.toLowerCase().includes(query);
        const matchesIngredient = recipe.ingredients?.some((ing) =>
          ing.name?.toLowerCase().includes(query)
        );
        if (!matchesName && !matchesIngredient) return false;
      }

      if (selectedCategory !== 'all') {
        const recipeCatId = getRecipeCategoryId(recipe);
        if (recipeCatId == null || Number(recipeCatId) !== Number(selectedCategory)) {
          return false;
        }
      }

      if (activeQuickFilters.length > 0) {
        const matchesAnyQuickFilter = activeQuickFilters.some(filterId => {
          const quickFilter = QUICK_FILTERS.find(f => f.id === filterId);
          return quickFilter ? quickFilter.filter(recipe) : false;
        });
        if (!matchesAnyQuickFilter) return false;
      }

      const foodCostPct =
        recipe.sellingPrice > 0 ? (recipe.totalCost / recipe.sellingPrice) * 100 : 0;
      if (foodCostRange.min && foodCostPct < parseFloat(foodCostRange.min)) return false;
      if (foodCostRange.max && foodCostPct > parseFloat(foodCostRange.max)) return false;

      if (costRange.min && recipe.totalCost < parseFloat(costRange.min)) return false;
      if (costRange.max && recipe.totalCost > parseFloat(costRange.max)) return false;

      const profit = recipe.sellingPrice - recipe.totalCost;
      if (profitRange.min && profit < parseFloat(profitRange.min)) return false;
      if (profitRange.max && profit > parseFloat(profitRange.max)) return false;

      return true;
    });

    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.menuItemName || '').localeCompare(b.menuItemName || '');
        case 'name_desc':
          return (b.menuItemName || '').localeCompare(a.menuItemName || '');
        case 'cost_asc':
          return a.totalCost - b.totalCost;
        case 'cost_desc':
          return b.totalCost - a.totalCost;
        case 'profit_asc':
          return (a.sellingPrice - a.totalCost) - (b.sellingPrice - b.totalCost);
        case 'profit_desc':
          return (b.sellingPrice - b.totalCost) - (a.sellingPrice - a.totalCost);
        case 'foodcost_asc':
          return a.sellingPrice && b.sellingPrice
            ? a.totalCost / a.sellingPrice - b.totalCost / b.sellingPrice
            : 0;
        case 'foodcost_desc':
          return a.sellingPrice && b.sellingPrice
            ? b.totalCost / b.sellingPrice - a.totalCost / a.sellingPrice
            : 0;
        case 'updated_desc':
          return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
        case 'updated_asc':
          return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [
    recipes,
    searchQuery,
    selectedCategory,
    costRange,
    profitRange,
    foodCostRange,
    sortBy,
  ]);

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setCostRange({ min: '', max: '' });
    setProfitRange({ min: '', max: '' });
    setFoodCostRange({ min: '', max: '' });
    setActiveQuickFilters([]);
  };

  // Clear only advanced range filters
  const clearAdvancedFilters = () => {
    setCostRange({ min: '', max: '' });
    setProfitRange({ min: '', max: '' });
    setFoodCostRange({ min: '', max: '' });
  };

  // ── Gated handlers ─────────────────────────────────────────────────────────
  const handleAddRecipe = () => {
    if (!canAdd) return;
    setSelectedRecipe(null);
    setDuplicateIngredients(null);
    setEditorOpen(true);
  };

  const handleEditRecipe = (recipe) => {
    if (!canEdit) return;
    setSelectedRecipe(recipe);
    setDuplicateIngredients(null);
    setEditorOpen(true);
  };

  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setViewerOpen(true);
  };

  const handleDuplicateRecipe = (recipe) => {
    if (!canDuplicate) return;
    setSelectedRecipe(null);
    setDuplicateIngredients({
      ingredients: recipe.ingredients || [],
      prepNotes: recipe.prepNotes || '',
      sourceRecipeName: recipe.menuItemName,
    });
    setViewerOpen(false);
    setEditorOpen(true);
  };

  const handleDeleteRecipe = (recipe) => {
    if (!canDelete) return;
    setConfirmDialog({
      isOpen: true,
      type: 'delete',
      data: recipe,
    });
  };

  const confirmDelete = async () => {
    try {
      const recipe = confirmDialog.data;
      if (!recipe) return;
      await deleteRecipe(recipe.id);
      showToast(`Recipe "${recipe.menuItemName}" deleted`, "success");
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      showToast("Failed to delete recipe", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: '', data: null });
    }
  };

  const confirmConfig =
    confirmDialog.type === 'delete'
      ? {
          title: 'Delete Recipe',
          message: 'Are you sure you want to delete this recipe? This action cannot be undone.',
          confirmText: 'Delete',
          type: 'danger',
          onConfirm: confirmDelete,
        }
      : null;

  const handleSaveRecipe = async (formData) => {
    try {
      if (selectedRecipe) {
        await updateRecipe(selectedRecipe.id, formData);
        showToast("Recipe updated successfully", "success");
      } else {
        await createRecipe(formData);
        showToast("Recipe created successfully", "success");
      }
      setEditorOpen(false);
      setSelectedRecipe(null);
      setDuplicateIngredients(null);
    } catch (error) {
      console.error('Failed to save recipe:', error);
      showToast("Failed to save recipe", "error");
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        refreshItems(),
        fetchRecipes(selectedBranchId),
        (async () => {
          try {
            const data = await categoryService.getAll(selectedBranchId);
            setCategories(data || []);
          } catch (error) {
            console.error('Failed to refresh categories:', error);
          }
        })(),
      ]);
      showToast("Recipes refreshed", "success");
    } catch (error) {
      console.error('Failed to refresh data:', error);
      showToast("Failed to refresh data", "error");
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  const anyLoading = isLoading || menuLoading || recipesLoading;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-slate-900">Recipe Management</h1>
          <div className="flex items-center gap-2">
            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => { setSelectedBranchId(e.target.value || null); setSelectedCategory('all'); }}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}
            <button
              onClick={handleRefresh}
              disabled={anyLoading}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={18} className={anyLoading ? 'animate-spin' : ''} />
            </button>
            {/* Add Recipe — only if permitted */}
            {canAdd && (
              <button
                onClick={handleAddRecipe}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm"
              >
                <Plus size={16} />
                Add Recipe
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar - Redesigned */}
      <div className="bg-white border-b border-slate-200">
        {/* Main Filter Row */}
        <div className="px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search recipes or ingredients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-slate-50 focus:bg-white transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Quick Filter Chips */}
            <div className="flex items-center gap-2 flex-wrap flex-1">
              {QUICK_FILTERS.map((filter) => {
                const Icon = filter.icon;
                const isActive = activeQuickFilters.includes(filter.id);
                const colorClasses = {
                  emerald: isActive ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'hover:bg-emerald-50 hover:border-emerald-200',
                  amber: isActive ? 'bg-amber-100 text-amber-700 border-amber-300' : 'hover:bg-amber-50 hover:border-amber-200',
                  teal: isActive ? 'bg-teal-100 text-teal-700 border-teal-300' : 'hover:bg-teal-50 hover:border-teal-200',
                  red: isActive ? 'bg-red-100 text-red-700 border-red-300' : 'hover:bg-red-50 hover:border-red-200',
                  violet: isActive ? 'bg-violet-100 text-violet-700 border-violet-300' : 'hover:bg-violet-50 hover:border-violet-200',
                  slate: isActive ? 'bg-slate-200 text-slate-700 border-slate-400' : 'hover:bg-slate-100 hover:border-slate-300',
                };

                return (
                  <button
                    key={filter.id}
                    onClick={() => toggleQuickFilter(filter.id)}
                    title={filter.description}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium
                      border transition-all duration-150
                      ${isActive
                        ? colorClasses[filter.color]
                        : `bg-white text-slate-600 border-slate-200 ${colorClasses[filter.color]}`
                      }
                    `}
                  >
                    <Icon size={12} />
                    {filter.label}
                    {isActive && <Check size={10} className="ml-0.5" />}
                  </button>
                );
              })}
            </div>

            {/* Advanced Filters Toggle Button */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${showAdvancedFilters
                  ? 'bg-teal-600 text-white shadow-md'
                  : hasAdvancedFilters
                    ? 'bg-teal-50 text-teal-700 border border-teal-200'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                }
              `}
            >
              <Filter size={16} />
              <span>{showAdvancedFilters ? 'Hide Filters' : 'More Filters'}</span>
              {hasAdvancedFilters && !showAdvancedFilters && (
                <span className="bg-teal-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {(costRange.min || costRange.max ? 1 : 0) +
                   (profitRange.min || profitRange.max ? 1 : 0) +
                   (foodCostRange.min || foodCostRange.max ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <ArrowUpDown size={14} />
                {SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Sort'}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                        sortBy === option.value
                          ? 'text-teal-600 bg-teal-50'
                          : 'text-slate-700'
                      }`}
                    >
                      {option.label}
                      {sortBy === option.value && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear All Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
              >
                <X size={14} />
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Advanced Filters - Expandable Container */}
        {showAdvancedFilters && (
          <div className="border-t border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
            <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={16} className="text-teal-600" />
                <span className="text-sm font-semibold text-slate-800">Advanced Filters</span>
                {hasAdvancedFilters && (
                  <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {(costRange.min || costRange.max ? 1 : 0) +
                     (profitRange.min || profitRange.max ? 1 : 0) +
                     (foodCostRange.min || foodCostRange.max ? 1 : 0)} active
                  </span>
                )}
              </div>
              {hasAdvancedFilters && (
                <button
                  onClick={clearAdvancedFilters}
                  className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-white transition-colors"
                >
                  <RotateCcw size={12} />
                  Reset Filters
                </button>
              )}
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Cost Range */}
                <div className="space-y-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 uppercase tracking-wide">
                    <DollarSign size={12} className="text-blue-500" />
                    Cost Range (₹)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={costRange.min}
                      onChange={(e) => setCostRange((prev) => ({ ...prev, min: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="text-slate-400 text-sm">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={costRange.max}
                      onChange={(e) => setCostRange((prev) => ({ ...prev, max: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>

                {/* Profit Range */}
                <div className="space-y-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 uppercase tracking-wide">
                    <TrendingUp size={12} className="text-emerald-500" />
                    Profit Range (₹)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={profitRange.min}
                      onChange={(e) => setProfitRange((prev) => ({ ...prev, min: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="text-slate-400 text-sm">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={profitRange.max}
                      onChange={(e) => setProfitRange((prev) => ({ ...prev, max: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>

                {/* Food Cost % Range */}
                <div className="space-y-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 uppercase tracking-wide">
                    <Percent size={12} className="text-amber-500" />
                    Food Cost (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min %"
                      value={foodCostRange.min}
                      onChange={(e) => setFoodCostRange((prev) => ({ ...prev, min: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="text-slate-400 text-sm">to</span>
                    <input
                      type="number"
                      placeholder="Max %"
                      value={foodCostRange.max}
                      onChange={(e) => setFoodCostRange((prev) => ({ ...prev, max: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Filter Tags Row */}
        {hasActiveFilters && !showAdvancedFilters && (
          <div className="px-6 pb-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500 font-medium">Active:</span>

            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                Search: "{searchQuery.length > 15 ? searchQuery.slice(0, 15) + '...' : searchQuery}"
                <button onClick={() => setSearchQuery('')} className="hover:text-red-600 ml-0.5">
                  <X size={10} />
                </button>
              </span>
            )}

            {activeQuickFilters.map(filterId => {
              const filter = QUICK_FILTERS.find(f => f.id === filterId);
              if (!filter) return null;
              return (
                <span key={filterId} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                  {filter.label}
                  <button onClick={() => toggleQuickFilter(filterId)} className="hover:text-red-600 ml-0.5">
                    <X size={10} />
                  </button>
                </span>
              );
            })}

            {(costRange.min || costRange.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                Cost: ₹{costRange.min || '0'} - ₹{costRange.max || '∞'}
                <button onClick={() => setCostRange({ min: '', max: '' })} className="hover:text-red-600 ml-0.5">
                  <X size={10} />
                </button>
              </span>
            )}

            {(profitRange.min || profitRange.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                Profit: ₹{profitRange.min || '0'} - ₹{profitRange.max || '∞'}
                <button onClick={() => setProfitRange({ min: '', max: '' })} className="hover:text-red-600 ml-0.5">
                  <X size={10} />
                </button>
              </span>
            )}

            {(foodCostRange.min || foodCostRange.max) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full">
                Food Cost: {foodCostRange.min || '0'}% - {foodCostRange.max || '100'}%
                <button onClick={() => setFoodCostRange({ min: '', max: '' })} className="hover:text-red-600 ml-0.5">
                  <X size={10} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content with Sidebar */}
      <div className="flex-1 overflow-hidden flex">
        {/* Category Sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0 overflow-y-auto">
          <div className="p-3 bg-slate-50 border-b border-slate-200">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
              Categories
            </h3>
          </div>

          <nav className="p-2 space-y-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-teal-50 text-teal-700 shadow-sm'
                  : categoryHasSearchMatch('all')
                  ? 'bg-yellow-50 text-slate-700 border border-yellow-300'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Grid3x3 className="w-4 h-4" />
                All Recipes
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === 'all'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {getCategoryCount('all')}
              </span>
            </button>

            {categoriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="text-slate-400 animate-spin" />
              </div>
            ) : (
              <div className="pt-2 border-t border-slate-100 space-y-1">
                {categories.map((category) => {
                  const count = getCategoryCount(category.id);
                  const isActive = String(selectedCategory) === String(category.id);
                  const hasSearchMatch = categoryHasSearchMatch(category.id);

                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                        isActive
                          ? 'bg-teal-50 text-teal-700 shadow-sm'
                          : hasSearchMatch
                          ? 'bg-yellow-50 text-slate-700 border border-yellow-300'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate flex-1 text-left pr-2">
                        {category.name}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          isActive
                            ? 'bg-teal-100 text-teal-700'
                            : hasSearchMatch
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {anyLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={40} className="text-teal-500 animate-spin mb-4" />
              <p className="text-slate-500">Loading recipes...</p>
            </div>
          ) : filteredRecipes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 rounded-2xl bg-teal-50 flex items-center justify-center mb-6">
                <ChefHat size={40} className="text-teal-400" />
              </div>
              {recipes.length === 0 ? (
                <>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    No Recipes Yet
                  </h3>
                  <p className="text-slate-500 mb-6 max-w-md">
                    Create your first recipe to start tracking ingredient costs
                    and food cost percentages.
                  </p>
                  {/* Empty-state CTA — only if permitted */}
                  {canAdd && (
                    <button
                      onClick={handleAddRecipe}
                      className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                    >
                      <Plus size={18} />
                      Create First Recipe
                    </button>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    No Matching Recipes
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Try adjusting your filters to find recipes.
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    >
                      Clear All Filters
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onView={() => handleViewRecipe(recipe)}
                  // Pass undefined for actions the user can't perform — RecipeCard hides those buttons
                  onEdit={canEdit ? () => handleEditRecipe(recipe) : undefined}
                  onDelete={canDelete ? () => handleDeleteRecipe(recipe) : undefined}
                  onDuplicate={canDuplicate ? () => handleDuplicateRecipe(recipe) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer with count */}
      {!anyLoading && recipes.length > 0 && (
        <div className="bg-white border-t border-slate-200 px-6 py-2">
          <p className="text-xs text-slate-500">
            {selectedCategory !== 'all' || hasActiveFilters
              ? `Showing ${filteredRecipes.length} of ${recipes.length} recipes`
              : `${recipes.length} recipe${recipes.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      )}

      {/* Recipe Editor — only if add or edit permitted */}
      {editorOpen && (canAdd || canEdit) && (
        <RecipeEditor
          recipe={selectedRecipe}
          menuItems={menuItems}
          duplicateData={duplicateIngredients}
          onSave={handleSaveRecipe}
          onClose={() => {
            setEditorOpen(false);
            setSelectedRecipe(null);
            setDuplicateIngredients(null);
          }}
        />
      )}

      {/* Recipe Viewer — always accessible (read-only); restricted actions passed as undefined */}
      {viewerOpen && selectedRecipe && (
        <RecipeViewer
          recipe={selectedRecipe}
          onEdit={canEdit ? () => { setViewerOpen(false); setEditorOpen(true); } : undefined}
          onDuplicate={canDuplicate ? () => handleDuplicateRecipe(selectedRecipe) : undefined}
          onClose={() => {
            setViewerOpen(false);
            setSelectedRecipe(null);
          }}
        />
      )}

      {/* Confirm dialog */}
      {confirmConfig && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, type: '', data: null })}
          {...confirmConfig}
        />
      )}
    </div>
  );
}